// Marco del panel: riel de iconos en escritorio, barra inferior en móvil
// (≤5 destinos, con icono Y etiqueta). La navegación no cambia de sitio entre
// pantallas — el usuario siempre sabe dónde está y cómo salir.
import { NavLink, Outlet } from "react-router-dom";
import { LogOut, Moon, Sun } from "lucide-react";
import { DESTINOS } from "./destinos.ts";

import { Campanita, FranjaAlertas, LogoVXAI, useAlertas } from "@victorix/chat-ai-ui";
import { useSesion } from "./lib/auth.tsx";
import { sb } from "./lib/supabase.ts";
import { aplicarTema, temaGuardado } from "./lib/tema.ts";
import { useEffect, useState } from "react";
import { aplicarFavicon, marcaDe } from "./lib/marca.ts";

// La barra inferior de móvil aguanta 5 con la etiqueta legible; en 320 px un
// sexto las apretaría hasta romperlas. Por eso "Probar bot" va marcado como
// `soloEscritorio`: es herramienta de configuración —se usa sentado, no en el
// celular atendiendo— y sigue accesible por el riel y por URL.
// Cuando entre Campañas (C5), este es el sitio donde se decide qué cede.


const EN_MOVIL = DESTINOS.filter((d) => !d.soloEscritorio);

function BotonTema() {
  const [tema, setTema] = useState(temaGuardado());
  const alternar = () => {
    const oscuroAhora = document.documentElement.dataset.tema === "oscuro";
    const nuevo = oscuroAhora ? "claro" : "oscuro";
    aplicarTema(nuevo); setTema(nuevo);
  };
  return (
    <button onClick={alternar} title="Cambiar tema" aria-label="Cambiar entre claro y oscuro"
      className="h-10 w-10 grid place-items-center rounded-lg text-white/55 hover:text-white
        hover:bg-white/10 active:scale-90 transition cursor-pointer">
      {tema === "oscuro" ? <Sun size={18} strokeWidth={1.75} /> : <Moon size={18} strokeWidth={1.75} />}
    </button>
  );
}

export default function Shell() {
  const { membresias, tenantId, elegirTenant, salir, correo, rol } = useSesion();
  // El riel esconde «Equipo» a quien no es dueño. Es cosmética: quien escriba la
  // URL a mano igual llega, y ahí lo para la pantalla — y, sobre todo, la base.
  const esDueno = rol === "dueno" || rol === "victorix";

  // La marca del negocio activo (p. ej. el escudo del colegio). Sin marca
  // propia, el panel lleva la de VX AI como siempre. La pestaña la sigue.
  const marca = marcaDe(membresias.find((m) => m.tenant_id === tenantId)?.slug);
  useEffect(() => { aplicarFavicon(marca); return () => aplicarFavicon(null); }, [marca]);

  // ⭐⭐ T-ALE-15 · UN SOLO ORIGEN PARA LA FRANJA Y LA CAMPANITA. Se llama AQUÍ,
  // una vez, y el estado baja a las dos. Si cada una consultara por su lado,
  // dar por visto un incidente en la campanita lo dejaría pintado en la franja
  // hasta 60 s y el panel se contradiría a sí mismo en pantalla.
  // ⚠️ Se le pasa el MISMO cliente con la sesión del usuario que usa el resto
  // del panel — quien reparte quién ve qué es la RLS, no esto.
  const alertas = useAlertas(sb, rol === "victorix");

  return (
    <div className="h-pantalla flex flex-col md:flex-row overflow-hidden">
      {/* Riel oscuro — escritorio */}
      <nav aria-label="Secciones" className="hidden md:flex flex-col items-center gap-1 w-[68px] shrink-0
        bg-[var(--color-rail)] py-3">
        <NavLink to="/" className="mb-3 px-2" title={marca ? marca.alt : "Victorix Chat AI"}>
          {marca
            ? <img src={marca.logo} alt={marca.alt} className="h-11 w-11 object-contain" />
            : <LogoVXAI className="h-5 w-auto" variant="onDark" />}
        </NavLink>
        {DESTINOS.filter((d) => !d.soloDueno || esDueno).map(({ a, t, I }) => (
          <NavLink key={a} to={a} end={a === "/"} title={t}
            className={({ isActive }) => `relative h-12 w-12 grid place-items-center rounded-xl
              transition-colors active:scale-90 cursor-pointer
              ${isActive ? "bg-white/12 text-white" : "text-white/50 hover:text-white hover:bg-white/8"}`}>
            {({ isActive }) => (<>
              <I size={20} strokeWidth={1.75} />
              {isActive && <span className="absolute left-0 top-3 bottom-3 w-0.5 rounded-full bg-marca" />}
              <span className="sr-only">{t}</span>
            </>)}
          </NavLink>
        ))}
        <div className="mt-auto flex flex-col items-center gap-1">
          {marca && <LogoVXAI className="h-3 w-auto mb-2 opacity-45" variant="onDark" />}
          <BotonTema />
          <button onClick={salir} title={`Salir (${correo ?? ""})`} aria-label="Cerrar sesión"
            className="h-10 w-10 grid place-items-center rounded-lg text-white/55 hover:text-white
              hover:bg-white/10 active:scale-90 transition cursor-pointer">
            <LogOut size={18} strokeWidth={1.75} />
          </button>
        </div>
      </nav>

      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* T-ALE-14 (R-ALE-01) · ARRIBA DE TODO y en TODAS las pantallas: va
            fuera del <Outlet/>, encima incluso de la barra del negocio activo.
            Se le pasa el MISMO cliente con la sesión del usuario que usa el
            resto del panel — quien reparte quién ve qué es la RLS, no esto. */}
        <FranjaAlertas estado={alertas} />

        {/* Barra superior: negocio activo (solo si hay más de uno) + marca en móvil */}
        <header className="shrink-0 flex items-center gap-3 px-3 md:px-4 h-12 border-b border-borde-suave
          bg-superficie">
          {marca
            ? <img src={marca.logo} alt={marca.alt} className="md:hidden h-8 w-8 object-contain" />
            : <LogoVXAI className="md:hidden h-[18px] w-auto" />}
          {membresias.length > 1 ? (
            <select value={tenantId ?? ""} onChange={(e) => elegirTenant(e.target.value)}
              aria-label="Negocio activo"
              className="h-8 px-2 rounded-lg bg-elevado border border-borde text-[13px] cursor-pointer
                focus:outline-none focus:border-marca/50">
              {membresias.map((m) => <option key={m.tenant_id} value={m.tenant_id}>{m.nombre}</option>)}
            </select>
          ) : (
            <span className="text-[13px] font-medium truncate">{membresias[0]?.nombre ?? "—"}</span>
          )}
          <span className="ml-auto microlabel hidden sm:block">
            {rol === "dueno" ? "Dueño" : rol === "victorix" ? "Victorix" : "Agente"}
          </span>
          {/* T-ALE-15 (R-ALE-02) · La campanita lleva TODO lo abierto —graves y
              avisos— con su «ya lo vi». La franja de arriba se queda solo con
              los graves, que son los que tienen que estorbar.
              ⚠️ `ml-auto` vive en la etiqueta del rol, que se esconde en móvil;
              por eso la campanita lleva el suyo, para que no se pegue al select
              del negocio en pantallas estrechas. */}
          <div className="ml-auto sm:ml-0"><Campanita estado={alertas} /></div>
          <button onClick={salir} aria-label="Cerrar sesión" title="Salir"
            className="md:hidden p-2 -mr-1 rounded-md text-tinta-2 active:scale-90 transition cursor-pointer">
            <LogOut size={17} strokeWidth={1.75} />
          </button>
        </header>

        <main className="flex-1 min-h-0 min-w-0"><Outlet /></main>

        {/* Barra inferior — móvil */}
        <nav aria-label="Secciones" className="md:hidden shrink-0 flex border-t border-borde-suave
          bg-superficie pb-[env(safe-area-inset-bottom)]">
          {EN_MOVIL.map(({ a, corto, t, I }) => (
            <NavLink key={a} to={a} end={a === "/"} aria-label={t}
              className={({ isActive }) => `flex-1 flex flex-col items-center gap-0.5 py-2 min-h-[52px]
                justify-center cursor-pointer active:scale-95 transition
                ${isActive ? "text-marca" : "text-tinta-2"}`}>
              <I size={19} strokeWidth={1.75} />
              <span className="text-[10.5px] font-medium">{corto}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
